import {useCallback, useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {X} from "lucide-react";
import {useTranslation} from "../../../shared/i18n";

const focusableElements = (container) => Array.from(container.querySelectorAll(
    "button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])",
));

const CredentialRotationDialogContent = ({entity, pending, error, onCancel, onConfirm}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const descriptionId = useId();
    const dialogRef = useRef(null);
    const passwordRef = useRef(null);
    const confirmationRef = useRef(null);
    const invokingElementRef = useRef(null);
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [validationError, setValidationError] = useState("");

    const clearSecrets = useCallback(() => {
        if (passwordRef.current) passwordRef.current.value = "";
        if (confirmationRef.current) confirmationRef.current.value = "";
        setPassword("");
        setConfirmation("");
        setValidationError("");
    }, []);

    const cancel = useCallback(() => {
        clearSecrets();
        onCancel();
    }, [clearSecrets, onCancel]);

    useEffect(() => {
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() => passwordRef.current?.focus());
        return () => {
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!pending) cancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [cancel, pending]);

    const handleSubmit = (event) => {
        event.preventDefault();
        const nextPassword = password.trim();
        if (!nextPassword) {
            setValidationError(t("orgs.credential.enter"));
            return;
        }
        if (nextPassword !== confirmation) {
            setValidationError(t("orgs.credential.mismatch"));
            return;
        }
        clearSecrets();
        onConfirm(nextPassword);
    };

    const message = validationError || error?.message;

    return createPortal(
        <div className="finance-dialog-backdrop" onMouseDown={(event) => {
            if (event.target === event.currentTarget && !pending) cancel();
        }}>
            <section
                ref={dialogRef}
                className="finance-dialog organization-credential-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
            >
                <form onSubmit={handleSubmit}>
                    <header className="finance-dialog-header">
                        <div>
                            <p className="finance-dialog-entity">{entity}</p>
                            <h2 id={titleId}>{t("orgs.credential.title")}</h2>
                        </div>
                        <button
                            className="finance-icon-button"
                            type="button"
                            aria-label={t("orgs.common.close")}
                            title={t("orgs.common.close")}
                            disabled={pending}
                            onClick={cancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>
                    <p id={descriptionId}>{t("orgs.credential.copy")}</p>
                    <label className="organization-dialog-field">
                        <span>{t("orgs.credential.newPassword")}</span>
                        <input
                            ref={passwordRef}
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            disabled={pending}
                            onChange={(event) => setPassword(event.target.value)}
                        />
                    </label>
                    <label className="organization-dialog-field">
                        <span>{t("orgs.credential.repeat")}</span>
                        <input
                            ref={confirmationRef}
                            type="password"
                            autoComplete="new-password"
                            value={confirmation}
                            disabled={pending}
                            onChange={(event) => setConfirmation(event.target.value)}
                        />
                    </label>
                    {message ? <div className="finance-dialog-error" role="alert"><p>{message}</p></div> : null}
                    <footer className="finance-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={pending} onClick={cancel}>
                            {t("orgs.common.cancel")}
                        </button>
                        <button type="submit" className="admin-button admin-button-primary" disabled={pending}>
                            {pending ? t("orgs.credential.pending") : t("orgs.credential.title")}
                        </button>
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};

export const CredentialRotationDialog = ({open, ...props}) => (
    open ? <CredentialRotationDialogContent {...props} /> : null
);
